import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCommanderStatsHealth } from '../../../../data/redux/slices/commanderCardStatsSlice';

/**
 * Pure: does this commander have usable commander-tuned stats?
 *   true  — at least one bin populated, tuned modes will return data
 *   false — below threshold / disabled / pending: tuned modes can't run
 *   null  — health not loaded yet (treat optimistically)
 * Single source of truth shared by UpgradelizerPane and UpgradeCTA so the
 * "is this tuned preset runnable?" decision can't drift between them.
 */
export function computeTunedAvailable(health) {
    if (!health) return null;
    const status = health.status;
    if (status === 'below_threshold' || status === 'disabled' || status === 'pending') return false;
    const bins = health.availableBins || {};
    return Object.values(bins).some((b) => b && (b.deckCount ?? 0) > 0);
}

/**
 * Resolves tuned-stats availability for a commander, fetching the health
 * probe on demand (guarded so multiple callers on one pane don't refetch).
 * Returns true / false / null per computeTunedAvailable.
 */
export function useCommanderTunedAvailable(commanderId) {
    const dispatch = useDispatch();
    const health = useSelector((s) => (commanderId ? s.commanderCardStats.healthById[commanderId] : null));
    const fetching = useSelector((s) =>
        commanderId ? Boolean(s.commanderCardStats.healthFetching[commanderId]) : false
    );
    useEffect(() => {
        if (commanderId && !health && !fetching) dispatch(fetchCommanderStatsHealth(commanderId));
    }, [commanderId, health, fetching, dispatch]);
    return computeTunedAvailable(health);
}
